import { useTranslation } from "@repo/core";
import type { PageProps } from "../types";

export const ResultsPage = ({ rebalanceResults }: PageProps) => {
  const { tStrategy } = useTranslation();

  if (!rebalanceResults) {
    return (
      <div className="text-center py-8">
        <span className="text-gray-500">{tStrategy("noResultsDataAvailable")}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Summary */}
      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
        <h4 className="text-lg font-semibold text-green-800 mb-2">
          {tStrategy("rebalanceSummary")}
        </h4>
        <p className="text-green-700">
          {tStrategy("successfulOrders")} {rebalanceResults.orders_ok?.length || 0}
        </p>
        {rebalanceResults.problematic_orders && (
          <p className="text-red-700 mt-1">
            {tStrategy("problematicOrdersCount")}{" "}
            {rebalanceResults.problematic_orders.length || 0}
          </p>
        )}
      </div>

      {/* New Positions Table */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-3">
          {tStrategy("actualPositions")}
        </h4>

        {rebalanceResults.positions && rebalanceResults.positions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                    {tStrategy("ticker")}
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                    {tStrategy("side")}
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                    {tStrategy("volume")}
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                    {tStrategy("openPrice")}
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                    {tStrategy("profit")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rebalanceResults.positions.map((position, index) => (
                  <tr
                    key={`${position.ticker}-${index}`}
                    className="hover:bg-gray-50"
                  >
                    <td className="border border-gray-300 px-4 py-2 font-medium">
                      {position.ticker}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          position.side.toLowerCase() === "long"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {position.side}
                      </span>
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {position.volume}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      ${position.open_price.toFixed(2)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <span
                        className={`font-medium ${
                          position.profit >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        ${position.profit.toFixed(2)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 py-4">{tStrategy("noActualPositions")}</p>
        )}
      </div>

      {/* Problematic Orders Table */}
      {rebalanceResults.problematic_orders && (
        <div>
          <h4 className="text-lg font-medium text-red-600 mb-3">
            {tStrategy("problematicOrders")}
          </h4>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-red-50">
                  <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                    {tStrategy("ticker")}
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                    {tStrategy("errorMessage")}
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                    {tStrategy("action")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rebalanceResults.problematic_orders.map((order) => (
                  <tr className="hover:bg-red-50" key={order.ticker}>
                    <td className="border border-gray-300 px-4 py-2 font-medium">
                      {order.ticker}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {order.message.msg}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                        {order.action}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
